import React, { useMemo } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LegendValueFormatter,
} from "recharts";
import { groupBy } from "lodash-es";

import { IWithballData } from "types";
import { calculatePecentage, groupPossessionStrive } from "utils";
import { StatisticsBoard } from "components/StatisticsBoard";
import { BAR_COLOR } from "MuiProvider/colors";

interface IPossessionPanelProps {
  dataArray: IWithballData[];
}

const ASPECT = 2;
const CHART_WIDTH = "100%";
const BAR_SIZE = 20;
const MARGIN = { top: 5, right: 5, left: 5, bottom: 5 };

export const PossessionPanel = ({ dataArray }: IPossessionPanelProps) => {
  const { win, lose } = useMemo(() => {
    return groupPossessionStrive(dataArray);
  }, [dataArray]);

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    const label = intl.get("STATS_LABEL_NUMBER");
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const renderPossessionChart = (
    winPossession: boolean = false,
    selfHalf: boolean = false
  ) => {
    const typedData = winPossession ? win : lose;
    const typedPositionedData = selfHalf
      ? typedData.selfHalf
      : typedData.otherHalf;
    const groupedData = groupBy(typedPositionedData, (data) => data.fromPlayer);
    const chartData = Object.getOwnPropertyNames(groupedData).map(
      (playerId) => ({
        name: groupedData[playerId][0].fromName,
        total: groupedData[playerId].length,
      })
    );

    if (chartData.length <= 0) {
      return null;
    }

    return (
      <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
        <BarChart data={chartData} margin={MARGIN}>
          <XAxis dataKey="name" angle={-45} interval={0} />
          <YAxis />
          <Tooltip />
          <Legend formatter={legendFormatter} />
          <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_POSSESSION_WIN")}: ${
          win.total
        }`}</Typography>
        <Box mt={1} />
        <Typography>{`${intl.get("STATS_POS_SELF")}: ${
          win.selfHalf.length
        } (${calculatePecentage(win.selfHalf.length, win.total)})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={win.selfHalf} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {renderPossessionChart(true, true)}
          </Grid>
        </Grid>
        <Typography>{`${intl.get("STATS_POS_OTHER")}: ${
          win.otherHalf.length
        } (${calculatePecentage(
          win.otherHalf.length,
          win.total
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={win.otherHalf} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {renderPossessionChart(true, false)}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_POSSESSION_LOSE")}: ${
          lose.total
        }`}</Typography>
        <Box mt={1} />
        <Typography>{`${intl.get("STATS_POS_SELF")}: ${
          lose.selfHalf.length
        } (${calculatePecentage(
          lose.selfHalf.length,
          lose.total
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={lose.selfHalf} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {renderPossessionChart(false, true)}
          </Grid>
        </Grid>
        <br />
        <Typography>{`${intl.get("STATS_POS_OTHER")}: ${
          lose.otherHalf.length
        } (${calculatePecentage(
          lose.otherHalf.length,
          lose.total
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={lose.otherHalf} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {renderPossessionChart(false, false)}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
